// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import type { LedgerState } from '../../types.js';
import type { ReviewedTransaction } from './types.js';

export interface AccountSuggestion {
  accountId: string;
  confidence: number; // fraction of matching txns that used this account (0–1)
  matchCount: number;
}

// Lowercase, strip non-alpha, split, keep words >= 3 chars
export function tokenize(description: string): Set<string> {
  return new Set(
    description
      .toLowerCase()
      .replace(/[^a-z]/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length >= 3),
  );
}

export function jaccardSimilarity(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const t of a) if (b.has(t)) intersection++;
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

const SIMILARITY_THRESHOLD = 0.25;

/**
 * Given an imported transaction description and the bank account it's being
 * imported into, scan existing ledger transactions for similar payees and
 * return the most-used counterpart account.
 */
export function suggestAccount(
  description: string,
  bankAccountId: string,
  ledger: LedgerState,
): AccountSuggestion | null {
  const query = tokenize(description);
  const counts = new Map<string, number>();

  for (const tx of ledger.transactions.values()) {
    if (tx.voided) continue;

    const sim = jaccardSimilarity(query, tokenize(tx.description));
    if (sim < SIMILARITY_THRESHOLD) continue;

    // Only consider transactions that touch the bank account
    const hasBankSplit = tx.splits.some((s) => s.accountId === bankAccountId);
    if (!hasBankSplit) continue;

    for (const split of tx.splits) {
      if (split.accountId === bankAccountId) continue;
      // Skip imbalance accounts — they're the fallback, not a useful suggestion
      const acct = ledger.accounts.get(split.accountId);
      if (!acct || acct.name.toLowerCase().startsWith('imbalance')) continue;
      counts.set(split.accountId, (counts.get(split.accountId) ?? 0) + 1);
    }
  }

  if (counts.size === 0) return null;

  let bestId = '';
  let bestCount = 0;
  for (const [id, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestId = id;
    }
  }

  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return { accountId: bestId, confidence: bestCount / total, matchCount: bestCount };
}

/**
 * Apply payee-based account suggestions to a list of reviewed transactions.
 * Only sets transferAccountId when none is already present.
 */
export function applyPayeeSuggestions(
  transactions: ReviewedTransaction[],
  ledger: LedgerState,
): ReviewedTransaction[] {
  return transactions.map((tx) => {
    if (tx.transferAccountId) return tx;
    const suggestion = suggestAccount(tx.description, tx.accountId, ledger);
    if (!suggestion) return tx;
    return { ...tx, transferAccountId: suggestion.accountId, suggestedByMatcher: true };
  });
}
